import {Inject, Injectable, InjectionToken, Injector, Optional, SkipSelf} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {distinctUntilChanged, map, switchMap} from 'rxjs/operators';
import {ValidationContext, ValidationContextField} from '../data/validation-context';
import {IValidationConfiguration, VALIDATION_CONFIGURATION} from '../types';

/**
 * The only thing this service does - it stores list of all global validation contexts.
 */
@Injectable()
export class ValidationService {
    protected readonly contexts$ = new BehaviorSubject<Record<string, ValidationContext>>({});

    public constructor(
        @Inject(ValidationService) @Optional() @SkipSelf()
        protected readonly existingValidationService: ValidationService | null = null,
        $injector: Injector,
        @Inject(VALIDATION_CONFIGURATION) @Optional()
            $configurations: Array<IValidationConfiguration | InjectionToken<IValidationConfiguration>> = [],
    ) {
        if (Array.isArray($configurations)) {
            for (const configRecord of $configurations) {
                const config = configRecord instanceof InjectionToken ? $injector.get(configRecord) : configRecord;
                for (const contextName of Object.keys(config)) {
                    const context = this.getContextSnapshot(contextName) || new ValidationContext(contextName);
                    for (const fieldName of Object.keys(config[contextName])) {
                        const field = context.getFieldSnapshot(fieldName) || new ValidationContextField(fieldName);
                        for (const rule of config[contextName][fieldName]) {
                            field.addRule(rule);
                        }
                        context.changeField(fieldName, field);
                    }
                    this.changeContext(contextName, context);
                }
            }
        }
    }

    public getContext$($name: string, $inheritContext: boolean = true): Observable<ValidationContext | null> {
        const context$ = this.contexts$
            .pipe(
                map($ => $[$name] || null),
                distinctUntilChanged(),
            );

        // In case local context is not present try to return parent context if there are multiple instances of ValidationService.
        if ($inheritContext && this.existingValidationService) {
            return context$.pipe(
                switchMap($context => {
                    if (!$context) {
                        return this.existingValidationService.getContext$($name, true);
                    }
                    return of($context);
                }),
            );
        }

        return context$;
    }

    public getContextSnapshot($name: string): ValidationContext {
        return this.contexts$.value[$name];
    }

    public changeContext($name: string, $context: ValidationContext): void {
        this.contexts$.next({
            ...this.contexts$.value,
            [$name]: $context
        });
    }

    public removeContext($name: string): void {
        if (this.contexts$.value.hasOwnProperty($name)) {
            const newContexts = {...this.contexts$.value};
            delete newContexts[$name];
            this.contexts$.next(newContexts);
        }
    }

    // public getOrCreateContext$($name: string, $global: boolean = false): Observable<ValidationContext> {
    //     if (!this.contexts$.value.hasOwnProperty($name)) {
    //         this.contexts$.next({
    //             ...this.contexts$.value,
    //             [$name]: new ValidationContext($name)
    //         });
    //     }
    //
    //     return this.getContext$($name);
    // }

    public get rootValidationService(): ValidationService {
        return this.existingValidationService
            ? this.existingValidationService.rootValidationService
            : this;
    }
}
